import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import OCRUploadPanel from "./OCRUploadPanel";
import ManualEntryPanel from "./ManualEntryPanel";
import OCRPreviewForm from "./OCRPreviewForm";
import {
  ocrApplicationForm,
  uploadApplicationFormFile,
  submitApplication,
} from "../leadsApi";
import styles from "./ApplicationFormModal.module.scss";

const STEP_CHOICE = "choice";
const STEP_LOADING = "loading";
const STEP_PREVIEW = "preview";

/**
 * Modal to move lead from Contacted → Application Form Filled.
 * Two options: Upload & Autofill (OCR) or Fill Manually.
 * After OCR or manual entry, shows split view to review and submit.
 */
export default function ApplicationFormModal({
  open,
  onClose,
  leadId,
  enquiry = null,
  onSuccess,
}) {
  const [step, setStep] = useState(STEP_CHOICE);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [previewData, setPreviewData] = useState(null); // { data, warnings, previewUrl, file }
  const [previewUrlToRevoke, setPreviewUrlToRevoke] = useState(null);

  const resetAndClose = useCallback(() => {
    setStep(STEP_CHOICE);
    setPreviewData(null);
    if (previewUrlToRevoke) {
      URL.revokeObjectURL(previewUrlToRevoke);
      setPreviewUrlToRevoke(null);
    }
    onClose?.();
  }, [onClose, previewUrlToRevoke]);

  const handleUpload = useCallback(
    async (file) => {
      if (!leadId) return;
      setStep(STEP_LOADING);
      setLoadingMessage("Reading form using AI...");
      let urlToRevoke = null;
      try {
        const result = await ocrApplicationForm(leadId, file);
        const data = result.data || {};
        const warnings = result.warnings || [];
        // Preview: object URL for image; for PDF we could show first page or a placeholder
        const isImage = /\.(jpe?g|png)$/i.test(file.name);
        if (isImage) {
          urlToRevoke = URL.createObjectURL(file);
          setPreviewUrlToRevoke(urlToRevoke);
        }
        setPreviewData({
          data,
          warnings,
          previewUrl: urlToRevoke || null,
          file,
        });
        setStep(STEP_PREVIEW);
        if (warnings.length > 0) {
          toast.warning(warnings.join(" "));
        }
      } catch (e) {
        toast.error(e.message || "OCR failed");
        setStep(STEP_CHOICE);
        if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
      } finally {
        setLoadingMessage("");
      }
    },
    [leadId]
  );

  const handleEnterDetails = useCallback(() => {
    setPreviewData({ data: {}, warnings: [], previewUrl: null, file: null }); // ocr empty; enquiry passed via initialData
    setStep(STEP_PREVIEW);
  }, []);

  const handleSubmit = useCallback(
    async (formData) => {
      if (!leadId) return;
      let uploadedPath = "";
      if (previewData?.file) {
        try {
          const up = await uploadApplicationFormFile(leadId, previewData.file);
          uploadedPath = up.uploaded_document_path || "";
        } catch (e) {
          toast.error(e.message || "File upload failed");
          return;
        }
      }
      await submitApplication(leadId, {
        ...formData,
        uploaded_document_path: uploadedPath,
      });
      toast.success("Application form submitted. Lead moved to Application Form Filled.");
      onSuccess?.();
      resetAndClose();
    },
    [leadId, previewData, onSuccess, resetAndClose]
  );

  const handleBack = useCallback(() => {
    setStep(STEP_CHOICE);
    setPreviewData(null);
    if (previewUrlToRevoke) {
      URL.revokeObjectURL(previewUrlToRevoke);
      setPreviewUrlToRevoke(null);
    }
  }, [previewUrlToRevoke]);

  // Which form keys were filled by OCR (for "Filled by OCR" badge); name split into first_name, last_name
  const autoFilledKeys = React.useMemo(() => {
    const ocr = previewData?.data;
    if (!ocr) return new Set();
    const set = new Set();
    if ((ocr.first_name && String(ocr.first_name).trim()) || (ocr.last_name && String(ocr.last_name).trim())) {
      if (ocr.first_name && String(ocr.first_name).trim()) set.add("first_name");
      if (ocr.last_name && String(ocr.last_name).trim()) set.add("last_name");
    } else if (ocr.name && String(ocr.name).trim()) {
      set.add("first_name");
      set.add("last_name");
    }
    if (ocr.phone && String(ocr.phone).trim()) set.add("phone");
    if (ocr.email && String(ocr.email).trim()) set.add("email");
    if (ocr.date_of_birth && String(ocr.date_of_birth).trim()) set.add("date_of_birth");
    if (ocr.nic_passport && String(ocr.nic_passport).trim()) set.add("nic_passport");
    if (ocr.address && String(ocr.address).trim()) set.add("address");
    if (ocr.country_of_residency && String(ocr.country_of_residency).trim()) set.add("country_of_residency");
    if (ocr.telephone_number && String(ocr.telephone_number).trim()) set.add("telephone_number");
    if (ocr.gender && String(ocr.gender).trim()) set.add("gender");
    return set;
  }, [previewData?.data]);

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth={step === STEP_PREVIEW ? "md" : "sm"}
      fullWidth
      PaperProps={{ className: styles.modalPaper }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {step === STEP_CHOICE && "Move to Application Form Filled"}
        {step === STEP_LOADING && "Processing form…"}
        {step === STEP_PREVIEW && "Review & submit"}
      </DialogTitle>
      <DialogContent className={styles.content}>
        {step === STEP_CHOICE && (
          <div className={styles.panelsRow}>
            <OCRUploadPanel onUpload={handleUpload} />
            <ManualEntryPanel onEnterDetails={handleEnterDetails} />
          </div>
        )}
        {step === STEP_LOADING && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography color="text.secondary">{loadingMessage}</Typography>
          </Box>
        )}
        {step === STEP_PREVIEW && (
          <OCRPreviewForm
            leadId={leadId}
            enquiryName={enquiry ? `${enquiry.first_name || ""} ${enquiry.last_name || ""}`.trim() : ""}
            initialData={{
              enquiry: enquiry ? { ...enquiry } : {},
              ocr: previewData?.data || {},
            }}
            autoFilledKeys={autoFilledKeys}
            previewUrl={previewData?.previewUrl ?? null}
            warnings={previewData?.warnings ?? []}
            onSubmit={handleSubmit}
            onCancel={handleBack}
            submitLabel="Confirm & submit"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
